import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, Cell, ReferenceLine } from 'recharts';
import {
  CheckCircle, XCircle, Clock, Eye, TrendingUp, Calendar, TestTubes, Home, GitBranch, Play, TestTube, ChevronRight, Search, ExternalLink, History, AlertTriangle, Loader2, RefreshCcw
} from 'lucide-react';
import azureDevOpsService from '../src/services/azureDevOpsService';
import SkippedTestsPanel from './components/SkippedTestsPanel';
import TestResultsTable from './components/TestResultsTable';
import RobotReportButton from './components/RobotReportButton';
import SuiteVerifyButton from './components/SuiteVerifyButton';
import AllSuitesVerifiedBanner from './components/AllSuitesVerifiedBanner';
import { getStatusFromSuccessRate } from './config/statusConfig';
import { computeTestStats, computeSuccessRate, computeNetTestDelta, statusLabel, statusChartColor, matchesStatusFilter } from './utils/testStatus';
import AiAnalysisDrawer from './components/AiAnalysisDrawer';
import rootCauseService from '../src/services/rootCauseService';
import bugLinkService from '../src/services/bugLinkService';
import BatchRootCauseBar from './components/BatchRootCauseBar';
import SuiteAnalysisPanel from './components/SuiteAnalysisPanel';
import { analyzeSuite, getCachedSuiteAnalysis, writeSuiteAnalysisCache } from './services/aiSuiteAnalysisService';
import TestCaseSearchBar from './components/TestCaseSearchBar';

// Configuration des pipelines et test suites OSINT
const OSINT_CONFIG = [
  {
    pipelineId: 616,
    pipelineName: 'RF OSINT Browser',
    testSuiteName: 'RF Osint',
    testEnvironment: 'val9'
  },
  {
    pipelineId: 615,
    pipelineName: 'RF OSINT API',
    testSuiteName: 'RF osint-api',
    testEnvironment: 'val9'
  }
];

const VERSION_CONFIGS_OSINT = {
  "26.2": [
    {
      "pipelineId": 644,
      "pipelineName": "RF OSINT Browser 26.2",
      "testSuiteName": "RF Osint",
      "type": "robotframework"
    },
    {
      "pipelineId": 643,
      "pipelineName": "RF OSINT API 26.2",
      "testSuiteName": "RF osint-api",
      "type": "robotframework"
    }
  ],
  "26.1": [
    {
      "pipelineId": 616,
      "pipelineName": "RF OSINT Browser",
      "testSuiteName": "RF Osint",
      "type": "robotframework"
    },
    {
      "pipelineId": 615,
      "pipelineName": "RF OSINT API",
      "testSuiteName": "RF osint-api",
      "type": "robotframework"
    }
  ],
  "25.2": [
    {
      "pipelineId": 526,
      "pipelineName": "RF Browser 25.2",
      "testSuiteName": "RF Osint",
      "testSuiteFilter": "test results osint",
      "type": "robotframework"
    },
    {
      "pipelineId": 525,
      "pipelineName": "RF API 25.2",
      "testSuiteName": "RF osint-api",
      "testSuiteFilter": "test results osint",
      "type": "robotframework"
    }
  ]
};

// Router interne (comme Agentique)
const useRouter = () => {
  const [currentPath, setCurrentPath] = useState('/');
  const [uiPath, setUiPath] = useState('/');
  const [params, setParams] = useState({});
  const baseRef = useRef('');

  const safeDecode = val => { try { return decodeURIComponent(val); } catch { return val; } };

  const parse = path => {
    const clean = path.split('?')[0] || '/';
    const parts = clean.split('/').filter(Boolean);
    const p = {};
    if (parts[0] === 'pipeline' && parts[1]) {
      p.pipelineId = parts[1];
      if (parts[2] === 'build' && parts[3]) {
        p.buildId = parts[3];
        if (parts[4] === 'run' && parts[5]) {
          p.runId = parts[5];
          if (parts[6] === 'testsuite' && parts[7]) {
            p.testSuiteName = safeDecode(parts.slice(7).join('/'));
          }
        }
      }
    } else if (parts[0] === 'testcase' && parts[1]) {
      p.testcaseName = safeDecode(parts[1]);
      if (parts[2] === 'history' && parts[3]) {
        p.pipelineId = parts[3];
        if (parts[4] === 'back') {
          p.backPipelineId = parts[5];
          if (parts[6] === 'build' && parts[7]) p.backBuildId = parts[7];
          if (parts[8] === 'run' && parts[9]) p.backRunId = parts[9];
          if (parts[10] === 'testsuite' && parts[11]) {
            p.backTestSuiteName = safeDecode(parts.slice(11).join('/'));
          }
        }
      }
    }
    return p;
  };

  const stripBase = path => {
    const base = baseRef.current;
    if (base && path.startsWith(base)) {
      const stripped = path.slice(base.length) || '/';
      return stripped.startsWith('/') ? stripped : `/${stripped}`;
    }
    return path || '/';
  };

  const applyPath = path => {
    const effective = stripBase(path);
    setCurrentPath(path);
    setUiPath(effective);
    setParams(parse(effective));
  };

  const navigate = path => {
    try {
      const full = `${baseRef.current || ''}${path}`;
      window.history.pushState({}, '', full);
    } catch {}
    applyPath(path);
  };

  useEffect(() => {
    const initialFull = (typeof window !== 'undefined' && window.location && (window.location.pathname + window.location.search)) || '/';
    try {
      const pathname = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
      baseRef.current = pathname.startsWith('/osint') ? '/osint' : '';
    } catch { baseRef.current = ''; }
    applyPath(initialFull);
    const onPop = () => {
      const pFull = window.location.pathname + window.location.search;
      applyPath(pFull);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return { currentPath, uiPath, params, navigate };
};

const StatusBadge = ({ status }) => {
  const styles = {
    succeeded: 'bg-green-100 text-green-800 border-green-300',
    success: 'bg-green-100 text-green-800 border-green-300',
    failed: 'bg-red-100 text-red-800 border-red-300',
    running: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    partiallysucceeded: 'bg-orange-100 text-orange-800 border-orange-300',
    passed: 'bg-green-100 text-green-800 border-green-300',
    skipped: 'bg-gray-100 text-gray-600 border-gray-300',
    notexecuted: 'bg-gray-100 text-gray-600 border-gray-300'
  };
  const icons = {
    success: <CheckCircle className="w-4 h-4" />,
    failed: <XCircle className="w-4 h-4" />,
    running: <Clock className="w-4 h-4" />,
    passed: <CheckCircle className="w-4 h-4" />,
    skipped: <Clock className="w-4 h-4" />,
    notexecuted: <Clock className="w-4 h-4" />
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.failed}`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const SuccessRateBadge = ({ successRate }) => {
  const normalizedRate = Number(String(successRate ?? '').replace('%', '')) || 0;
  const status = getStatusFromSuccessRate(normalizedRate, 0);
  const styles = {
    green: 'bg-green-100 text-green-800 border-green-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    red: 'bg-red-100 text-red-800 border-red-300'
  };
  const icons = {
    green: <CheckCircle className="w-4 h-4" />,
    yellow: <AlertTriangle className="w-4 h-4" />,
    red: <XCircle className="w-4 h-4" />
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status.color]}`}>
      {icons[status.color]}
      {status.label}
    </span>
  );
};

const formatDuration = seconds => {
  const minutes = Math.floor(seconds/60); const remainingSeconds = (seconds%60).toFixed(0); return `${minutes}m ${remainingSeconds}s`;
};
const formatDate = d => new Date(d).toLocaleString('fr-FR');

const Navigation = ({ currentPath, navigate }) => {
  const pathOnly = (currentPath||'/').split('?')[0];
  const pathParts = pathOnly.split('/').filter(Boolean);
  const breadcrumbs = [{ label:'OSINT', path:'/', icon:<Home className="w-4 h-4"/> }];
  if (pathParts[0] === 'pipeline' && pathParts[1]) {
    breadcrumbs.push({ label:pathParts[1], path:`/pipeline/${pathParts[1]}`, icon:<GitBranch className="w-4 h-4"/> });
    if (pathParts[2]==='build' && pathParts[3]) {
      breadcrumbs.push({ label:pathParts[3], path:`/pipeline/${pathParts[1]}/build/${pathParts[3]}`, icon:<Play className="w-4 h-4"/> });
      if (pathParts[4]==='run' && pathParts[5]) {
        breadcrumbs.push({ label:pathParts[5], path:`/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}`, icon:<TestTubes className="w-4 h-4"/> });
        if (pathParts[6]==='testsuite' && pathParts[7]) {
          const decodedTs = (()=>{ try { return decodeURIComponent(pathParts.slice(7).join('/')); } catch { return pathParts.slice(7).join('/'); } })();
          breadcrumbs.push({ label:decodedTs, path:`/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}/testsuite/${pathParts.slice(7).join('/')}`, icon:<TestTube className="w-4 h-4"/> });
        }
      }
    }
  }
  return <nav className="bg-white shadow-sm border-b mb-6"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><div className="flex items-center space-x-2 py-3">{breadcrumbs.map((crumb,i)=>(<React.Fragment key={crumb.path}>{i>0 && <ChevronRight className="w-4 h-4 text-gray-400"/>}<button onClick={()=>navigate(crumb.path)} className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">{crumb.icon}<span>{crumb.label}</span></button></React.Fragment>))}</div></div></nav>;
};

// BugsModal Component
const BugsModal = ({ showBugsModal, setShowBugsModal, modalBugs, modalBugTestName }) => {
  if (!showBugsModal) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowBugsModal(false)}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Bugs liés: {modalBugTestName}</h3>
        </div>
        <div className="p-6">
          <ul className="space-y-2">
            {modalBugs.map((bug, idx) => (
              <li key={idx}>
                <a
                  href={bug.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>{bug.id} - {bug.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={() => setShowBugsModal(false)}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// TestSuiteDetails (copié et adapté d'Agentique)
const TestSuiteDetails = ({ pipelineId, buildId, runId, testSuiteName, squadName = 'OSINT', navigate, initialSearchTerm = '' }) => {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [statusFilter, setStatusFilter] = useState('all');
  const [subSuiteFilter, setSubSuiteFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // États pour les bugs liés
  const [relatedBugsMap, setRelatedBugsMap] = useState({});
  const [showBugsModal, setShowBugsModal] = useState(false);
  const [modalBugs, setModalBugs] = useState([]);
  const [modalBugTestName, setModalBugTestName] = useState('');
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [selectedAiTestCase, setSelectedAiTestCase] = useState(null);
  const [showSuiteAnalysis, setShowSuiteAnalysis] = useState(false);
  const [suiteAnalysisResult, setSuiteAnalysisResult] = useState(null);
  const [isSuiteAnalyzing, setIsSuiteAnalyzing] = useState(false);
  const [suiteAnalysisError, setSuiteAnalysisError] = useState(null);

  const handleAnalyzeSuite = async () => {
    setIsSuiteAnalyzing(true);
    setSuiteAnalysisError(null);
    try {
      const result = await analyzeSuite('OSINT', testCases, testCases.length);
      setSuiteAnalysisResult(result);
    } catch (err) {
      setSuiteAnalysisError(err.message);
    } finally {
      setIsSuiteAnalyzing(false);
    }
  };

  const [nonReproMap, setNonReproMap] = useState(() => {
    try {
      const storageKey = `osint:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const data = JSON.parse(raw);
        const today = new Date().toDateString();
        if (data.date === today && data.values) {
          return data.values;
        }
      }
    } catch (e) {
      console.warn('Failed to load nonReproMap from localStorage:', e);
    }
    return {};
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await azureDevOpsService.getTestCasesForRun(runId, 'robotframework');
        setTestCases(results);
      } catch (e) {
        console.error('Error getTestCasesForRun', e);
        setError('Erreur chargement tests');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [runId]);

  // Fetch related bugs for all test cases
  useEffect(() => {
    if (!testCases || testCases.length === 0) return;
    
    const fetchBugs = async () => {
      const bugsMap = {};
      const failedTests = testCases.filter(t => t.status === 'failed');
      
      const fetchWithConcurrency = async (tasks, concurrency = 5) => {
        const results = [];
        for (let i = 0; i < tasks.length; i += concurrency) {
          const batch = tasks.slice(i, i + concurrency);
          const batchResults = await Promise.all(batch);
          results.push(...batchResults);
        }
        return results;
      };

      const tasks = failedTests.map(test => 
        azureDevOpsService.getRelatedBugsForTestCaseByTitle(test.name)
          .then(bugs => ({ testName: test.name, bugs }))
          .catch(err => {
            console.error(`Error fetching bugs for ${test.name}:`, err);
            return { testName: test.name, bugs: [] };
          })
      );

      const results = await fetchWithConcurrency(tasks, 5);
      results.forEach(({ testName, bugs }) => {
        if (bugs && bugs.length > 0) {
          bugsMap[testName] = bugs;
        }
      });

      try {
        const localMap = await bugLinkService.getForBuild(pipelineId, buildId);
        Object.entries(localMap).forEach(([testName, bugs]) => {
          const existing = bugsMap[testName] || [];
          const merged = [...existing];
          bugs.forEach(b => { if (!merged.some(m => m.id === b.id)) merged.push({ ...b, local: true }); });
          bugsMap[testName] = merged;
        });
      } catch (err) {
        console.warn('Erreur récupération bugs liés locaux:', err);
      }

      setRelatedBugsMap(bugsMap);
    };

    fetchBugs();
  }, [testCases, pipelineId, buildId]);

  // Persist nonReproMap to localStorage
  useEffect(() => {
    try {
      const storageKey = `osint:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const today = new Date().toDateString();
      const data = { date: today, values: nonReproMap };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save nonReproMap to localStorage:', e);
    }
  }, [nonReproMap, pipelineId, runId, testSuiteName]);

  const [nonReproLoading, setNonReproLoading] = useState(new Set());

  const markNonRepro = async (testName, testResultId) => {
    if (nonReproMap[testName] || nonReproLoading.has(testName)) return;
    setNonReproLoading(prev => new Set([...prev, testName]));
    try {
      await azureDevOpsService.updateTestResultOutcome(runId, testResultId);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } catch (err) {
      console.error('Erreur mise à jour AzDO non-reproduit:', err);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } finally {
      setNonReproLoading(prev => { const n = new Set(prev); n.delete(testName); return n; });
    }
  };

  const [rootCauseMap, setRootCauseMap] = useState({});
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [notesMap, setNotesMap] = useState({});
  useEffect(() => {
    if (!buildId) return;
    rootCauseService.getForBuild(pipelineId, buildId)
      .then(({ rootCauses, notes }) => { setRootCauseMap(rootCauses); setNotesMap(notes); })
      .catch(err => console.warn('Failed to load root causes:', err));
  }, [pipelineId, buildId]);
  const handleRootCause = async (testName, cause) => {
    const prev = rootCauseMap[testName];
    setRootCauseMap(m => ({ ...m, [testName]: cause }));
    try {
      if (cause) {
        await rootCauseService.set({ pipelineId, buildId, runId, testName, rootCause: cause, squad: squadName });
      } else {
        await rootCauseService.remove({ pipelineId, buildId, testName });
      }
    } catch (err) {
      console.error('Failed to save root cause:', err);
      setRootCauseMap(m => ({ ...m, [testName]: prev }));
    }
  };
  const handleBatchRootCause = async (cause) => {
    const names = [...selectedTests];
    const prev = { ...rootCauseMap };
    setRootCauseMap(m => {
      const next = { ...m };
      if (cause) names.forEach(nm => { next[nm] = cause; });
      else names.forEach(nm => { delete next[nm]; });
      return next;
    });
    try {
      await Promise.all(names.map(nm =>
        cause
          ? rootCauseService.set({ pipelineId, buildId, runId, testName: nm, rootCause: cause, squad: squadName })
          : rootCauseService.remove({ pipelineId, buildId, testName: nm })
      ));
    } catch (err) {
      console.error('Failed to save batch root cause:', err);
      setRootCauseMap(prev);
    }
    setSelectedTests(new Set());
  };

  const handleNoteSave = async (testName, notes) => {
    try {
      await rootCauseService.setNotes({ pipelineId, buildId, testName, notes });
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const [linkBugStatus, setLinkBugStatus] = useState(null);

  const linkOneBug = async (testName, bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      throw new Error(`#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})`);
    }
    let linked = summary;
    try {
      await azureDevOpsService.linkBugToTestCaseByTitle(testName, summary.id);
    } catch (err) {
      if (!/Test Case introuvable/.test(err.message || '')) throw err;
      // Pas de Test Case ADO pour ce test: on garde le lien côté app uniquement
      await bugLinkService.add({ pipelineId, buildId, testName, bug: summary });
      linked = { ...summary, local: true };
    }
    setRelatedBugsMap(prev => {
      const existing = prev[testName] || [];
      if (existing.some(b => b.id === linked.id)) return prev;
      return { ...prev, [testName]: [...existing, linked] };
    });
  };

  const handleBatchLinkBug = async (bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      setLinkBugStatus({ type: 'error', message: `#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})` });
      return;
    }

    const names = [...selectedTests].filter(
      nm => !(relatedBugsMap[nm] || []).some(b => b.id === summary.id)
    );

    if (names.length === 0) {
      setLinkBugStatus({ type: 'error', message: 'Tests déjà liés à ce bug' });
      return;
    }

    const linkedByTest = {};
    const failed = [];
    let localCount = 0;

    await Promise.all(names.map(async (nm) => {
      try {
        await azureDevOpsService.linkBugToTestCaseByTitle(nm, summary.id);
        linkedByTest[nm] = summary;
      } catch (err) {
        if (!/Test Case introuvable/.test(err.message || '')) {
          failed.push(nm);
          return;
        }
        try {
          await bugLinkService.add({ pipelineId, buildId, testName: nm, bug: summary });
          linkedByTest[nm] = { ...summary, local: true };
          localCount++;
        } catch {
          failed.push(nm);
        }
      }
    }));

    setRelatedBugsMap(prev => {
      const next = { ...prev };
      Object.entries(linkedByTest).forEach(([nm, bug]) => {
        const existing = next[nm] || [];
        next[nm] = existing.some(b => b.id === bug.id) ? existing : [...existing, bug];
      });
      return next;
    });

    const successCount = Object.keys(linkedByTest).length;
    setLinkBugStatus(
      failed.length > 0
        ? { type: 'error', message: `${successCount} test(s) lié(s), ${failed.length} échec(s)` }
        : { type: 'success', message: `${successCount} test(s) lié(s) au bug #${summary.id}${localCount ? ` (${localCount} en local, hors ADO)` : ''}` }
    );
    setSelectedTests(new Set());
  };

  const getSubSuite = (test) => test.automatedTestStorage ? test.automatedTestStorage.split('.').pop() : null;

  const subSuiteOptions = [...new Set(testCases.map(getSubSuite).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const filtered = testCases.filter(t => (
    t.name.toLowerCase().includes(searchTerm.toLowerCase())) &&
    matchesStatusFilter(t.status, statusFilter) &&
    (subSuiteFilter === 'all' || getSubSuite(t) === subSuiteFilter)
  ).sort((a, b) => {
    if (sortBy === 'duration') return (b.duration || 0) - (a.duration || 0);
    if (sortBy === 'status') return a.status.localeCompare(b.status);
    if (sortBy === 'subsuite') return (getSubSuite(a) || '').localeCompare(getSubSuite(b) || '');
    return a.name.localeCompare(b.name);
  });

  const { total, passed, failed, skipped, successRate } = computeTestStats(testCases);

  if (loading) return <div className="flex justify-center items-center h-64">Chargement...</div>;
  if (error) return <div className="text-red-600 text-center">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Suite: {testSuiteName}</h2>
            <p className="text-gray-600">Build: {buildId} | Run: {runId}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
              ← Retour
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
          {[
            { label: 'Total Tests', value: total, color: 'text-gray-900' },
            { label: 'Passés', value: passed, color: 'text-green-600' },
            { label: 'Échoués', value: failed, color: 'text-red-600' },
            { label: 'Ignorés', value: skipped, color: 'text-gray-500' },
            { label: 'Taux Réussite', value: successRate + '%', color: 'text-blue-600' }
          ].map(k => (
            <div key={k.label} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">{k.label}</span>
                <span className={`text-lg font-bold ${k.color}`}>{k.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <div className="px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Tests</h3>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-md"
              >
                <option value="all">Tous</option>
                <option value="passed">Passés</option>
                <option value="failed">Échoués</option>
                <option value="skipped">Ignorés</option>
              </select>
              <select
                value={subSuiteFilter}
                onChange={e => setSubSuiteFilter(e.target.value)}
                className="px-3 py-2 border rounded-md"
              >
                <option value="all">Toutes les suites</option>
                {subSuiteOptions.map(subSuite => (
                  <option key={subSuite} value={subSuite}>{subSuite}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="px-3 py-2 border rounded-md"
              >
                <option value="name">Nom</option>
                <option value="status">Statut</option>
                <option value="duration">Durée</option>
                <option value="subsuite">Suite</option>
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold">Tests Filtrés ({filtered.length})</h3>
            </div>
            <TestResultsTable
              tests={filtered}
              columns={{ suite: true, rootCause: true }}
              StatusBadge={StatusBadge}
              selectedTests={selectedTests}
              onSelectedTestsChange={setSelectedTests}
              getSubSuite={getSubSuite}
              relatedBugsMap={relatedBugsMap}
              nonReproMap={nonReproMap}
              nonReproLoading={nonReproLoading}
              onMarkNonRepro={markNonRepro}
              onLinkBug={linkOneBug}
              onShowBugs={(bugs, testName) => { setModalBugs(bugs); setModalBugTestName(testName); setShowBugsModal(true); }}
              rootCauseMap={rootCauseMap}
              notesMap={notesMap}
              onRootCause={handleRootCause}
              onNoteChange={(testName, val) => setNotesMap(m => ({ ...m, [testName]: val }))}
              onNoteBlur={handleNoteSave}
              renderActions={t => (
                <>
                  {t.logUrl && (
                    <a
                      href={t.logUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Log
                    </a>
                  )}
                  {t.status === 'failed' && (
                    <button
                      onClick={() => {
                        setSelectedAiTestCase(t);
                        setShowAiAnalysis(true);
                      }}
                      className="text-purple-600 hover:text-purple-800"
                      title={`Analyser ${t.name} avec l'IA`}
                    >
                      Analyser
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/testcase/${encodeURIComponent(t.name)}/history/${pipelineId}/back/${pipelineId}/build/${buildId}/run/${runId}/testsuite/${encodeURIComponent(testSuiteName)}`)}
                    className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    title="Voir l'historique"
                  >
                    <History className="w-3 h-3" />
                    Historique
                  </button>
                </>
              )}
              emptyState={(
                <div className="text-center py-12">
                  <TestTube className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun test</h3>
                  <p className="text-sm text-gray-500">Ajustez vos filtres.</p>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <BatchRootCauseBar
        count={selectedTests.size}
        onApply={handleBatchRootCause}
        onClear={() => { setSelectedTests(new Set()); setLinkBugStatus(null); }}
        onLinkBug={handleBatchLinkBug}
        linkBugStatus={linkBugStatus}
      />
      <AiAnalysisDrawer
        open={showAiAnalysis}
        onClose={() => {
          setShowAiAnalysis(false);
          setSelectedAiTestCase(null);
        }}
        testCase={selectedAiTestCase}
        context={{
          source: 'osint',
          pipelineId,
          buildId,
          runId,
          testSuiteName
        }}
      />
      <SuiteAnalysisPanel
        open={showSuiteAnalysis}
        onClose={() => setShowSuiteAnalysis(false)}
        squadName="OSINT"
        failedCount={failed}
        isLoading={isSuiteAnalyzing}
        error={suiteAnalysisError}
        result={suiteAnalysisResult}
        onAnalyze={handleAnalyzeSuite}
      />
      <BugsModal
        showBugsModal={showBugsModal}
        setShowBugsModal={setShowBugsModal}
        modalBugs={modalBugs}
        modalBugTestName={modalBugTestName}
      />
    </div>
  );
};

// Composant Historique Test Case
const RobotTestCaseHistory = ({ testcaseName, pipelineId, backPipelineId, backBuildId, backRunId, backTestSuiteName, navigate }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await azureDevOpsService.getAutomatedTestHistory(testcaseName, pipelineId);
        const correctedData = (data || []).map((item, index) => {
          const fullTimestamp = item.dateTimestamp || item.completedDate || item.startedDate || Date.now() - (index * 24 * 60 * 60 * 1000);
          const dateObj = new Date(fullTimestamp);
          return {
            ...item,
            dateTimestamp: fullTimestamp,
            date: item.date || dateObj.toISOString().split('T')[0],
            dateTime: dateObj.toISOString(),
            value: 1,
            statusDisplay: statusLabel(item.status)
          };
        })
        .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        setHistoryData(correctedData);
      } catch (e) {
        console.error('Erreur chargement historique test:', e);
        setError("Impossible de charger l'historique");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [testcaseName, pipelineId]);

  const filteredData = historyData.filter(item => {
    const matchStatus = matchesStatusFilter(item.status, statusFilter);
    const matchDate = !dateFilter || item.date === dateFilter;
    return matchStatus && matchDate;
  });

  const { total: totalTests, failed: failedTests, skipped: skippedTests, successRate } = computeTestStats(filteredData);
  const avgDuration = totalTests > 0 ? Math.round(filteredData.reduce((sum, d) => sum + (d.durationSeconds || d.duration || 0), 0) / totalTests) : 0;

  if (loading) return <div className="text-center py-8">Chargement de l'historique...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;
  if (historyData.length === 0) return <div className="text-center py-8 text-gray-500">Aucun historique trouvé pour ce test</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historique du Test</h2>
            <p className="text-gray-600">{testcaseName}</p>
          </div>
          <button 
            onClick={() => {
              if (backPipelineId && backBuildId && backRunId && backTestSuiteName) {
                const ts = encodeURIComponent(backTestSuiteName);
                navigate(`/pipeline/${backPipelineId}/build/${backBuildId}/run/${backRunId}/testsuite/${ts}`);
              } else {
                navigate(`/`);
              }
            }} 
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors">
            ← Retour
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          <select
            className="border px-2 py-1 rounded"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="passed">Passé</option>
            <option value="failed">Échoué</option>
            <option value="skipped">Ignoré</option>
          </select>

          <input
            type="date"
            className="border px-2 py-1 rounded"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />

          <button
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            onClick={() => { setStatusFilter('all'); setDateFilter(''); }}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalTests}</div>
          <div className="text-sm text-gray-600">Exécutions</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{successRate}%</div>
          <div className="text-sm text-gray-600">Taux de réussite</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{failedTests}</div>
          <div className="text-sm text-gray-600">Échecs</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-500">{skippedTests}</div>
          <div className="text-sm text-gray-600">Ignorés</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{avgDuration}ms</div>
          <div className="text-sm text-gray-600">Durée moyenne</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des Statuts d'Exécution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
            <YAxis domain={[0, 1]} tick={false} label={{ value: 'Statut', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value, name, props) => [props.payload.statusDisplay, 'Statut']} labelFormatter={(label) => `Date: ${label}`} />
            <Bar dataKey="value" name="Statut d'exécution">
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={statusChartColor(entry.status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution de la Durée</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} label={{ value: 'Durée (s)', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => [`${value}s`, 'Durée']} labelFormatter={(label) => `Date: ${label}`} />
            <Area type="monotone" dataKey="durationSeconds" stroke="#3b82f6" strokeWidth={2} fill="url(#colorDuration)" name="Durée d'exécution" />
            <defs>
              <linearGradient id="colorDuration" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Détails par Build</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Build</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((entry, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.dateTime).toLocaleString('fr-FR', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.buildId}</td>
                  <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={entry.status} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.duration}s</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {entry.logUrl && (
                      <a href={entry.logUrl} className="text-blue-600 hover:text-blue-800 flex items-center gap-1" target="_blank" rel="noreferrer">
                        <ExternalLink className="w-3 h-3" />
                        Voir Log
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Dashboard OSINT
const OsintDashboard = ({ navigate }) => {
  const [testSuites,setTestSuites]=useState([]); const [trendData,setTrendData]=useState([]); const [trendDays,setTrendDays]=useState(7); const [trendLoading,setTrendLoading]=useState(true); const [loading,setLoading]=useState(true); const [error,setError]=useState(null); const [runningPipelines,setRunningPipelines]=useState(new Map()); const [showFailingOnly, setShowFailingOnly] = useState(false);
  const [selectedTrendDate, setSelectedTrendDate] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(() => {
    try { return localStorage.getItem('osintVersion') || '26.1'; } catch { return '26.1'; }
  });
  const activeConfig = VERSION_CONFIGS_OSINT[selectedVersion] || VERSION_CONFIGS_OSINT['26.1'];
  useEffect(() => { try { localStorage.setItem('activeVersion', selectedVersion); } catch {} }, [selectedVersion]);
  useEffect(() => { setSelectedTrendDate(null); }, [selectedVersion]);
  const [triggerLoading, setTriggerLoading] = useState(new Set());
  const [showDashSuiteAnalysis, setShowDashSuiteAnalysis] = useState(false);
  const [dashSuiteAnalysisResult, setDashSuiteAnalysisResult] = useState(null);
  const [isDashSuiteAnalyzing, setIsDashSuiteAnalyzing] = useState(false);
  const [dashSuiteAnalysisError, setDashSuiteAnalysisError] = useState(null);
  const [selectedDashSuite, setSelectedDashSuite] = useState(null);
  const handleAnalyzeSuiteCard = async (suite, force = false) => {
    setSelectedDashSuite(suite);
    setShowDashSuiteAnalysis(true);
    if (!force) {
      const cached = getCachedSuiteAnalysis(suite.name, suite.runId);
      if (cached) {
        setDashSuiteAnalysisResult({ ...cached.result, _fromCache: true, _cachedAt: cached.savedAt });
        setDashSuiteAnalysisError(null);
        setIsDashSuiteAnalyzing(false);
        return;
      }
    }
    setIsDashSuiteAnalyzing(true);
    setDashSuiteAnalysisError(null);
    setDashSuiteAnalysisResult(null);
    try {
      const testCases = await azureDevOpsService.getTestCasesForRun(suite.runId);
      const result = await analyzeSuite(suite.name, testCases, testCases.length);
      writeSuiteAnalysisCache(suite.name, suite.runId, result);
      setDashSuiteAnalysisResult(result);
    } catch (err) {
      setDashSuiteAnalysisError(err.message);
    } finally {
      setIsDashSuiteAnalyzing(false);
    }
  };
  const handleTriggerPipeline = async (pipelineId) => {
    setTriggerLoading(prev => new Set([...prev, pipelineId]));
    try {
      await azureDevOpsService.triggerPipeline(pipelineId);
    } catch (err) {
      console.error('Failed to trigger pipeline:', err);
    } finally {
      setTriggerLoading(prev => { const s = new Set(prev); s.delete(pipelineId); return s; });
    }
  };
  const [globalStats,setGlobalStats]=useState({ totalTests:0,totalPassed:0,totalFailed:0,totalSkipped:0,averageSuccessRate:0,averageDuration:0 });

  // Builds interrogés pour la synthèse des tests ignorés (suites RF uniquement ici).
  const skippedBuilds = useMemo(
    () => [...new Set(testSuites.map(s => s.buildId).filter(Boolean))].map(buildId => ({ buildId })),
    [testSuites]
  );
  useEffect(()=>{ const cfg = VERSION_CONFIGS_OSINT[selectedVersion] || VERSION_CONFIGS_OSINT['26.1']; const fetchData=async()=>{ try { setLoading(true); setError(null); const suitesPromises = cfg.map(async c=>{ try { let latestBuild; if (selectedTrendDate) { const builds = await azureDevOpsService.getBuildsBySessionKey(c.pipelineId, selectedTrendDate); if (!builds.length) return null; latestBuild = builds[0]; } else { const buildsData = await azureDevOpsService.getRecentBuildsByPipeline(c.pipelineId); const builds = buildsData[`pipeline-${c.pipelineId}`]||[]; if (!builds.length) return null; latestBuild = builds[0]; } const testSuitesData = await azureDevOpsService.getTestSuitesForBuild(latestBuild.buildId); const allSuites = testSuitesData[`build-${latestBuild.buildId.replace('build-','')}`]||[]; const osintSuite = allSuites.find(s=> s.name === c.testSuiteName || (c.testSuiteFilter && s.name.toLowerCase().includes(c.testSuiteFilter.toLowerCase()))); if (osintSuite) { return { ...osintSuite, pipelineId:c.pipelineId, pipelineName:c.pipelineName, buildId:latestBuild.buildId, executionDate:latestBuild.date, }; } return null;} catch(e){ console.error('Erreur pipeline',c.pipelineId,e); return null;} }); const suites=(await Promise.all(suitesPromises)).filter(s=>s); setTestSuites(suites); let total=0,passed=0,failed=0,skipped=0,duration=0; suites.forEach(s=>{ total+=s.totalTests||0; passed+=s.passed||0; failed+=s.failed||0; skipped+=s.skipped||0; duration+=s.duration||0; }); setGlobalStats({ totalTests:total, totalPassed:passed, totalFailed:failed, totalSkipped:skipped, averageSuccessRate: computeSuccessRate(passed,total,skipped), averageDuration: suites.length? duration/suites.length:0 }); const runMap=new Map(); await Promise.all(cfg.map(async c=>{ const runningBuild=await azureDevOpsService.hasRunningBuild(c.pipelineId); if(runningBuild) runMap.set(c.pipelineId, runningBuild); })); setRunningPipelines(runMap); } catch(e){ console.error('Erreur chargement données OSINT',e); setError('Impossible de charger les données OSINT'); } finally { setLoading(false);} }; fetchData(); },[selectedVersion, selectedTrendDate]);
  useEffect(()=>{ const cfg = VERSION_CONFIGS_OSINT[selectedVersion] || VERSION_CONFIGS_OSINT['26.1']; const fetchTrend=async()=>{ setTrendLoading(true); try { const pipelineIds = cfg.map(c=>c.pipelineId); const suiteNames = cfg.flatMap(c=>c.testSuiteFilter ? [c.testSuiteName, c.testSuiteFilter] : [c.testSuiteName]); const data = await azureDevOpsService.getTrendDataBySessions(pipelineIds,trendDays,suiteNames); setTrendData(data);} catch(e){ console.error('Erreur tendance OSINT',e);} finally { setTrendLoading(false); } }; fetchTrend(); },[trendDays,selectedVersion]);
  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-center"><div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div><p className="text-gray-600">Chargement OSINT...</p></div></div>;
  if (error) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-center text-red-600"><XCircle className="w-12 h-12 mx-auto mb-4"/><p>{error}</p></div></div>;
  const rateColor = (rate) => rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-yellow-400' : 'bg-red-500';

  // Variation nette du nombre de tests sur la période, calculée suite par suite (cf. computeNetTestDelta).
  const { first: trendFirstTotal, last: trendLastTotal, delta: trendAddedTests } = computeNetTestDelta(trendData);
  const trendAddedTextColor = trendAddedTests > 0 ? 'text-green-600' : trendAddedTests < 0 ? 'text-orange-600' : 'text-gray-500';
  return <div className="min-h-screen bg-gray-100"><div className="bg-white shadow-sm border-b"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"><div className="flex items-center justify-between"><div><h1 className="text-3xl font-bold text-gray-900">Tests OSINT</h1><p className="text-gray-600 mt-1">Résultats des tests OSINT RobotFramework</p><div className="flex items-center gap-2 mt-3"><label className="text-sm text-gray-600">Version</label><select value={selectedVersion} onChange={e => { setSelectedVersion(e.target.value); localStorage.setItem('osintVersion', e.target.value); }} className="border border-gray-300 rounded-md px-2 py-1 text-sm"><option value="26.2">26.2</option><option value="26.1">26.1</option><option value="25.2">25.2</option></select></div></div><svg className="w-12 h-12 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 4-8 4-8-4 8-4z"/><path d="M4 11l8 4 8-4"/><path d="M4 16l8 4 8-4"/></svg></div></div></div><main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><div className="space-y-6"><div className="flex justify-end mb-2"><button onClick={() => window.location.reload()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-blue-600 transition-colors shadow-sm"><RefreshCcw className="w-3.5 h-3.5" />Rafraîchir</button></div><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">{[{label:'Total Tests',value:globalStats.totalTests,color:'text-gray-900',icon:<TestTubes className="w-8 h-8 text-gray-600"/>},{label:'Tests Passés',value:globalStats.totalPassed,color:'text-green-600',icon:<CheckCircle className="w-8 h-8 text-green-600"/>},{label:'Tests Échoués',value:globalStats.totalFailed,color:'text-red-600',icon:<XCircle className="w-8 h-8 text-red-600"/>},{label:'Tests Ignorés',value:globalStats.totalSkipped,color:'text-gray-500',icon:<Clock className="w-8 h-8 text-gray-400"/>},{label:'Taux de Réussite',value:(globalStats.averageSuccessRate.toFixed(1)+'%'),color:'text-blue-600',icon:<TrendingUp className="w-8 h-8 text-blue-600"/>}].map(card=>(<div key={card.label} className="bg-white rounded-lg shadow p-6"><div className="flex items-center"><div className="flex-1"><p className="text-sm font-medium text-gray-600">{card.label}</p><p className={`text-2xl font-bold ${card.color}`}>{card.value}</p></div>{card.icon}</div></div>))}</div>{globalStats.totalSkipped>0&&<SkippedTestsPanel builds={skippedBuilds} expectedTotal={globalStats.totalSkipped}/>}<AllSuitesVerifiedBanner suites={testSuites} version={selectedVersion}/>{runningPipelines.size>0&&<div className="bg-blue-50 border border-blue-200 rounded-lg p-4"><div className="flex items-center gap-2 mb-3"><Loader2 className="w-4 h-4 text-blue-600 animate-spin"/><span className="text-sm font-semibold text-blue-800">{runningPipelines.size} pipeline{runningPipelines.size>1?'s':''} en cours d'exécution</span></div><div className="flex flex-wrap gap-2">{activeConfig.filter(cfg=>runningPipelines.has(cfg.pipelineId)).map(cfg=>(<a key={cfg.pipelineId} href={runningPipelines.get(cfg.pipelineId).url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"><Play className="w-3 h-3"/>{cfg.pipelineName}<ExternalLink className="w-3 h-3"/></a>))}</div></div>}<div className="flex items-center justify-between mb-4"><span className="text-sm text-gray-600 font-medium">{testSuites.length} suite{testSuites.length !== 1 ? 's' : ''}</span><button onClick={() => setShowFailingOnly(!showFailingOnly)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${showFailingOnly ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}><XCircle className="w-3.5 h-3.5" />{showFailingOnly ? 'Tous les pipelines' : 'Pipelines en échec'}</button></div><div className="grid grid-cols-1 lg:grid-cols-2 gap-6">{testSuites.filter(suite => !showFailingOnly || suite.failed > 0).map(suite=>{const successRate=computeSuccessRate(suite.passed,suite.totalTests,suite.skipped);const isRunning=runningPipelines.has(suite.pipelineId);return(<div key={`${suite.pipelineId}-${suite.runId}`} className={`rounded-lg shadow p-6 ${isRunning?'bg-blue-50 border border-blue-200':'bg-white'}`}><div className="flex items-center justify-between mb-4"><div className="min-h-[5rem]"><h3 className="text-lg font-semibold text-gray-900">{suite.pipelineName}</h3><p className="text-sm text-gray-600 mt-1">{suite.name}</p></div><div className="flex items-center gap-2">{isRunning&&<a href={runningPipelines.get(suite.pipelineId).url} target="_blank" rel="noopener noreferrer" title="Voir la pipeline en cours sur Azure DevOps" className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"><Loader2 className="w-3 h-3 animate-spin"/>En cours<ExternalLink className="w-3 h-3"/></a>}<button onClick={()=>handleTriggerPipeline(suite.pipelineId)} disabled={triggerLoading.has(suite.pipelineId)} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200 disabled:opacity-50 transition-colors" title="Lancer le pipeline">{triggerLoading.has(suite.pipelineId)?<Loader2 className="w-3.5 h-3.5 animate-spin"/>:<Play className="w-3.5 h-3.5"/>}</button><SuccessRateBadge successRate={successRate}/></div></div><div className="space-y-3">{suite.executionDate && <div className="flex justify-between text-sm"><span className="text-gray-600 flex items-center gap-1"><Calendar className="w-4 h-4"/>Date:</span><span className="font-medium"><a href={`https://dev.azure.com/${azureDevOpsService.organization}/${azureDevOpsService.project}/_build/results?buildId=${suite.buildId?.replace('build-', '')}`} target="_blank" rel="noopener noreferrer" title="Voir le build sur Azure DevOps" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline">{formatDate(suite.executionDate)}<ExternalLink className="w-3 h-3"/></a>{suite.executionDate && (() => { const days = Math.floor((Date.now() - new Date(suite.executionDate).getTime()) / 86400000); if (days === 0) return null; return <span className={`ml-1 text-xs ${days > 5 ? 'text-orange-500' : 'text-gray-400'}`}>({days === 1 ? 'hier' : `il y a ${days}j`})</span>; })()}</span></div>}{suite.testEnvironment && (<div className="flex justify-between text-sm"><span className="text-gray-600">Environnement de test:</span><span className="font-medium">{suite.testEnvironment}</span></div>)}<div className="flex justify-between text-sm"><span className="text-gray-600">Build:</span><span className="font-medium">{suite.buildId}</span></div><div className="flex justify-between text-sm"><span className="text-gray-600">Tests:</span><span className="font-medium"><span className="text-green-600">{suite.passed}</span> / <span className="text-red-600">{suite.failed}</span> / {suite.skipped > 0 && <><span className="text-gray-500">{suite.skipped}</span> / </>}<span className="text-gray-900">{suite.totalTests}</span></span></div><div className="flex justify-between text-sm"><span className="text-gray-600 flex items-center gap-1"><Clock className="w-4 h-4"/>Durée:</span><span className="font-medium">{formatDuration(suite.duration)}</span></div><div className="flex justify-between text-sm"><span className="text-gray-600">Taux de réussite:</span><span className="font-medium">{successRate.toFixed(1)}%</span></div><div className="w-full bg-gray-200 rounded-full h-2 mt-2"><div className={`${rateColor(successRate)} h-2 rounded-full`} style={{width: `${successRate}%`}}></div></div>{suite.failed > 0 && <button onClick={() => handleAnalyzeSuiteCard(suite)} className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-medium transition">🤖 Analyser les échecs ({suite.failed})</button>}<button onClick={()=>navigate(`/pipeline/${suite.pipelineId}/build/${suite.buildId}/run/${suite.runId}/testsuite/${encodeURIComponent(suite.name)}`)} className="w-full mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"><Eye className="w-4 h-4"/>Voir Détails</button><div className="flex gap-2 mt-2"><RobotReportButton buildId={suite.buildId} kind="report" className="flex-1 justify-center"/><RobotReportButton buildId={suite.buildId} kind="log" className="flex-1 justify-center"/></div><SuiteVerifyButton pipelineId={suite.pipelineId} buildId={suite.buildId} runId={suite.runId} suiteName={suite.name} failed={suite.failed} totalTests={suite.totalTests} executionDate={suite.executionDate} version={selectedVersion}/></div></div>);})}
{activeConfig.filter(cfg=>runningPipelines.has(cfg.pipelineId)&&!testSuites.some(s=>s.pipelineId===cfg.pipelineId)).map(cfg=>(<div key={"placeholder-"+cfg.pipelineId} className="rounded-lg shadow p-6 bg-blue-50 border border-blue-200"><div className="flex items-center justify-between mb-4"><div className="min-h-[5rem]"><h3 className="text-lg font-semibold text-gray-900">{cfg.pipelineName}</h3><p className="text-sm text-gray-600 mt-1">{cfg.testSuiteName}</p></div><a href={runningPipelines.get(cfg.pipelineId).url} target="_blank" rel="noopener noreferrer" title="Voir la pipeline en cours sur Azure DevOps" className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"><Loader2 className="w-3 h-3 animate-spin"/>En cours<ExternalLink className="w-3 h-3"/></a></div><div className="py-6 text-center text-sm text-blue-600 italic">Exécution en cours, résultats pas encore disponibles...</div><button disabled className="w-full mt-2 px-4 py-2 rounded-md flex items-center justify-center gap-2 bg-gray-100 text-gray-400 cursor-not-allowed" title="Pas de résultats disponibles"><Eye className="w-4 h-4"/>Voir Détails</button></div>))}
</div>{testSuites.length===0 && <div className="bg-white rounded-lg shadow p-12 text-center"><TestTubes className="w-16 h-16 text-gray-400 mx-auto mb-4"/><h3 className="text-lg font-medium text-gray-900 mb-2">Aucune suite OSINT</h3><p className="text-gray-600">Non disponible pour le moment.</p></div>}{(trendLoading || trendData.length > 0) && (
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Évolution des Tests OSINT</span>
                  {selectedTrendDate && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                      <Calendar className="w-3 h-3" />
                      {trendData.find(d => d._key === selectedTrendDate)?.date || selectedTrendDate}
                      <button onClick={() => setSelectedTrendDate(null)} className="ml-1 hover:text-blue-900">×</button>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!trendLoading && trendData.length > 1 && (
                    <span
                      className="mr-2 text-xs text-gray-500"
                      title={`${trendFirstTotal} → ${trendLastTotal} tests, suite par suite (1re → dernière exécution de la période) : variation nette, ajouts moins suppressions. Une suite vue une seule fois dans la période n'est pas comptée.`}
                    >
                      Tests ajoutés : <span className={`font-semibold ${trendAddedTextColor}`}>{trendAddedTests > 0 ? '+' : ''}{trendAddedTests}</span>
                    </span>
                  )}
                  {[7, 14, 21].map(d => (
                    <button key={d} onClick={() => setTrendDays(d)}
                      className={`px-2 py-0.5 text-xs rounded transition-colors ${trendDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200'}`}>
                      {d}j
                    </button>
                  ))}
                </div>
              </div>
              {!selectedTrendDate && <p className="text-xs text-gray-400 mb-1">Cliquez sur une barre pour voir les résultats de ce jour</p>}
              {trendLoading ? (
                <div className="h-52 flex items-center justify-center">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={trendData}
                    margin={{ top: 4, right: 4, bottom: 40, left: 0 }}
                    style={{ cursor: 'pointer' }}
                    onClick={(data) => {
                      const key = data?.activePayload?.[0]?.payload?._key;
                      if (key) setSelectedTrendDate(prev => prev === key ? null : key);
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" />
                    <YAxis tick={{ fontSize: 9 }} width={28} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const entry = payload[0].payload;
                      const dayRate = computeSuccessRate(entry.success, entry.total, entry.skipped);
                      const dayRateColor = dayRate >= 90 ? 'text-green-600' : dayRate >= 70 ? 'text-yellow-600' : 'text-red-600';
                      return (
                        <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                          <p className="font-semibold">{entry.date}</p>
                          <p className="text-green-600">✓ {entry.success}</p>
                          <p className="text-red-500">✗ {entry.failed}</p>
                          <p className="text-gray-600">= {entry.total}</p>
                          <p className="text-gray-600">Taux : <span className={`font-semibold ${dayRateColor}`}>{dayRate.toFixed(1)}%</span></p>
                          <p className="text-gray-400 mt-1">Cliquez pour voir les résultats</p>
                        </div>
                      );
                    }} />
                    <Bar dataKey="success" stackId="a" fill="#10b981">
                      {trendData.map((entry, idx) => (
                        <Cell key={idx} fill={entry._key === selectedTrendDate ? '#059669' : '#10b981'} />
                      ))}
                    </Bar>
                    <Bar dataKey="failed" stackId="a" fill="#ef4444">
                      {trendData.map((entry, idx) => (
                        <Cell key={idx} fill={entry._key === selectedTrendDate ? '#b91c1c' : '#ef4444'} />
                      ))}
                    </Bar>
                    {trendData.length > 0 && (
                      <ReferenceLine
                        y={Math.max(...trendData.map(d => d.total))}
                        stroke="#94a3b8"
                        strokeDasharray="4 3"
                        label={{ value: `max ${Math.max(...trendData.map(d => d.total))}`, position: 'insideTopRight', fontSize: 9, fill: '#94a3b8' }}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}</div></main><SuiteAnalysisPanel open={showDashSuiteAnalysis} onClose={() => setShowDashSuiteAnalysis(false)} squadName={selectedDashSuite?.name || 'Suite'} failedCount={selectedDashSuite?.failed || 0} isLoading={isDashSuiteAnalyzing} error={dashSuiteAnalysisError} result={dashSuiteAnalysisResult} onAnalyze={() => selectedDashSuite && handleAnalyzeSuiteCard(selectedDashSuite, true)} /></div>;
};

const OsintPage = () => {
  const { uiPath, params, navigate } = useRouter();
  const [pendingFilter, setPendingFilter] = useState('');
  const pipelineIds = [...new Set(Object.values(VERSION_CONFIGS_OSINT).flat().map(c => c.pipelineId))];
  const renderContent = () => {
    if (uiPath === '/') return <OsintDashboard navigate={navigate} />;
    if (uiPath.startsWith('/testcase/') && params.testcaseName && params.pipelineId) {
      return <RobotTestCaseHistory testcaseName={params.testcaseName} pipelineId={params.pipelineId} backPipelineId={params.backPipelineId} backBuildId={params.backBuildId} backRunId={params.backRunId} backTestSuiteName={params.backTestSuiteName} navigate={navigate} />;
    }
    if (uiPath.startsWith('/pipeline/') && params.pipelineId) {
      if (params.runId && params.testSuiteName) {
        return <TestSuiteDetails key={`${params.pipelineId}-${params.runId}-${params.testSuiteName}`} pipelineId={params.pipelineId} buildId={params.buildId} runId={params.runId} testSuiteName={params.testSuiteName} navigate={navigate} initialSearchTerm={pendingFilter} />;
      }
    }
    return <div className="text-red-600">Page non trouvée</div>;
  };
  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation currentPath={uiPath} navigate={navigate} />
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <TestCaseSearchBar
            onSelect={async (result) => {
              setPendingFilter(result.title);
              try {
                const location = await azureDevOpsService.findTestCaseLocation(result.id, result.title, pipelineIds);
                if (location) {
                  navigate(`/pipeline/${location.pipelineId}/build/${location.buildId}/run/${location.runId}/testsuite/${encodeURIComponent(location.suiteName)}`);
                  return;
                }
              } catch (e) {
                console.warn('findTestCaseLocation échec:', e);
              }
              navigate('/');
            }}
            pendingFilter={pendingFilter}
            onClearFilter={() => setPendingFilter('')}
          />
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">{renderContent()}</main>
    </div>
  );
};

export default OsintPage;
